import React, { memo, ReactElement, useMemo } from 'react';

import { Col, Form, Row, TypographyText, Divider } from '@components/atoms';
import InputField from '@components/molecules/InputField';
import { codeMap } from '@constants/code';
import { TAnswer } from '@constants/enum';
import { TEXT_AREA_PLACEHOLDER } from '@constants/text';
import { useReduxForm } from '@hooks/useReduxForm';
import _ from 'lodash';

import Onset from './Onset';
import Scale from './Scale';

function Check({ formKey, answers }: { formKey: string; answers: TAnswer[] }): ReactElement {
  const { form, formData, formFieldsError, validationChecker, setFormData } = useReduxForm('survey');
  const { surveyType } = formData ?? {};
  const span = 24;
  const twoColumnsInRowFlag = false;

  const b2cFlag = useMemo(() => surveyType === 'B2C', [surveyType]);

  return (
    <Row gutter={[0, 5]} style={{ rowGap: '6px' }}>
      {(b2cFlag ? answers.filter(({ value }) => value !== -1) : answers).map(({ value, label, next }) => {
        const checked: any =
          typeof formData[formKey] === 'number'
            ? formData[formKey] === value
            : formData[formKey]?.includes(value);
        return (
          <>
            <Col
              onClick={() => {
                if (checked) {
                  // 선택됐던 항목 (해제 해야함)
                  if (formData[formKey] === 0) {
                    // 아무 증상 없음 항목
                    // 아무것도 눌리지 않은 상태처럼 해당 설문 초기화 해야함
                    const body = {
                      [formKey]: undefined,
                      [`${formKey}Next`]: undefined,
                    };
                    setFormData(body);
                  } else {
                    // 아무 증상 없음 외의 유의미한 항목을 해제 하는 경우
                    const exceptArr = formData[formKey].filter((e: number) => e !== value);
                    const body = {
                      [formKey]: exceptArr.length ? exceptArr : undefined, // 값을 제외한 배열이 비어 있는 경우 값 초기화
                    };
                    if (next) {
                      // next 값이 있는 경우에만 다음 설문 세팅
                      const nextArr = _.isArray(next) ? next : [next]; // nextArr type: string[]
                      const exceptNextArr = formData[`${formKey}Next`]?.filter(
                        (e: any) => !nextArr.includes(e),
                      );
                      // 값을 제외한 배열이 비어 있는 경우 값 초기화
                      body[`${formKey}Next`] = exceptNextArr?.length ? exceptNextArr : undefined;
                      nextArr.forEach((n: string) => {
                        if (['Scale', 'Onset'].includes(codeMap.get(n)?.selectType as string)) {
                          body[n] = undefined;
                        }
                      });
                    }
                    setFormData(body);
                  }
                } else if (value === 0) {
                  // 아무 증상 없음 항목
                  const body = {
                    [formKey]: 0,
                    [`${formKey}Next`]: 0,
                  };
                  setFormData(body);
                } else if (formData[formKey] === 0 || formData[formKey] === undefined) {
                  // 유의미한 응답이고 기존 설문 응답 값이 0이거나 없는 경우
                  const body = {
                    [formKey]: [value],
                  };
                  if (next !== undefined) {
                    // next 값이 있는 경우에만 다음 설문을 어디로 세팅할지 지정
                    const nextArr = _.isArray(next) ? next : [next]; // nextArr type: string[]
                    const ScaleOnsetSurvey = nextArr.filter((n: string) =>
                      ['Scale', 'Onset'].includes(codeMap.get(n)?.selectType as string),
                    );
                    const v = nextArr.filter(
                      (n: string) => !['Scale', 'Onset'].includes(codeMap.get(n)?.selectType as string),
                    );
                    // @ts-ignore
                    body[`${formKey}Next`] = v?.length ? v : undefined;
                    ScaleOnsetSurvey.forEach((n: string) => {
                      if (codeMap.get(n)?.selectType === 'Scale') {
                        // @ts-ignore
                        body[n] = { scale: 3 };
                      } else if (codeMap.get(n)?.selectType === 'Onset') {
                        // @ts-ignore
                        body[n] = { onset: 0 };
                      }
                    });
                  }
                  setFormData(body);
                } else {
                  // 유의미한 응답이고 기존 설문 응답 값이 유의미한 value들의 배열로 이뤄진 경우
                  const body = {
                    [formKey]: [...formData[formKey], value].sort(),
                  };
                  if (next !== undefined) {
                    // next 값이 있는 경우에만 다음 설문을 어디로 세팅할지 지정
                    const nextArr = _.isArray(next) ? next : [next];
                    const ScaleOnsetSurvey = nextArr.filter((n: string) =>
                      ['Scale', 'Onset'].includes(codeMap.get(n)?.selectType as string),
                    );
                    const filteredNextArr = nextArr.filter(
                      (n: string) => !['Scale', 'Onset'].includes(codeMap.get(n)?.selectType as string),
                    );
                    const v = _.isArray(formData[`${formKey}Next`])
                      ? _.uniq([...formData[`${formKey}Next`], ...filteredNextArr]).sort()
                      : [...filteredNextArr];
                    // @ts-ignore
                    body[`${formKey}Next`] = v?.length ? v : undefined;
                    ScaleOnsetSurvey.forEach((n: string) => {
                      if (codeMap.get(n)?.selectType === 'Scale') {
                        // @ts-ignore
                        body[n] = { scale: 3 };
                      } else if (codeMap.get(n)?.selectType === 'Onset') {
                        // @ts-ignore
                        body[n] = { onset: 0 };
                      }
                    });
                  }
                  setFormData(body);
                }
              }}
              span={span}
              twoColumnsInRowFlag={twoColumnsInRowFlag}
              yzTypeCol={checked ? 'selected-check-button' : 'check-button'}
            >
              <Row style={{ height: '45.5px', display: 'flex', alignItems: 'center' }}>
                <Col span={3} style={{ margin: 'auto', textIndent: '20px' }}>
                  {checked ? (
                    <img
                      alt={`${formKey}-checked`}
                      src={`${process.env.PUBLIC_URL}/img/checked-icon.webp`}
                      style={{ width: '16px', cursor: 'pointer' }}
                    />
                  ) : (
                    <img
                      alt={`${formKey}-unchecked`}
                      src={`${process.env.PUBLIC_URL}/img/unchecked-icon.webp`}
                      style={{ width: '16px', cursor: 'pointer' }}
                    />
                  )}
                </Col>
                <Col
                  span={21}
                  style={{
                    display: 'flex',
                    margin: 'auto',
                    paddingLeft: `${twoColumnsInRowFlag ? `25px` : `7px`}`,
                  }}
                >
                  <TypographyText yzTypeTypographyText="basic">{label}</TypographyText>
                </Col>
              </Row>
            </Col>
            {!b2cFlag && checked && codeMap.get(value as string)?.selectType === 'Scale' && (
              <Scale value={value as string} />
            )}
            {!b2cFlag && checked && codeMap.get(value as string)?.selectType === 'Onset' && (
              <Onset value={value as string} />
            )}
            {!b2cFlag && checked && value === -1 && (
              <Form
                form={form}
                initialValues={{ [`${formKey}Desc`]: formData?.[`${formKey}Desc`] ?? '' }}
                labelAlign="left"
                style={{ width: '100%' }}
              >
                <InputField
                  borderTop={false}
                  componentArray={[
                    {
                      type: 'textArea',
                      yzTypeTextArea: 'surveyItem',
                      placeholder: TEXT_AREA_PLACEHOLDER,
                      dataIndex: `${formKey}Desc`,
                      formData,
                      onChange: (e: any) => {
                        setFormData(`${formKey}Desc`, e);
                      },
                    },
                  ]}
                  formFieldsError={formFieldsError}
                  validation={validationChecker}
                />
              </Form>
            )}
          </>
        );
      })}
    </Row>
  );
}

export default memo(Check);
